// The option list behind the leads boards' "Sort by:" control.
//
// The dropdown and the sortable column headers are two ways to drive ONE sort
// state, so the option list must cover every state a header click can reach -
// both directions of every sortable column. Anything less needs a "Column sort"
// entry naming a state the list of names cannot express, which is what this
// replaced: clicking Address showed "Column sort" rather than "Address (Z-A)".
// Correspondingly the headers cycle desc <-> asc only; an unsorted third state
// would be another unnameable one.

export type SortDirection = "asc" | "desc";

// One sortable column. Both labels are required: a column with wording for only
// one direction is a column whose other direction the dropdown cannot name.
export interface SortColumnSpec<K extends string> {
  key: K;
  descLabel: string;
  ascLabel: string;
}

export interface SortOption<K extends string> {
  value: string;
  label: string;
  key: K;
  dir: SortDirection;
}

export const sortOptionValue = (key: string, dir: SortDirection): string =>
  `${key}:${dir}`;

export const buildSortOptions = <K extends string>(
  columns: SortColumnSpec<K>[]
): SortOption<K>[] =>
  columns.flatMap((c) => [
    {
      value: sortOptionValue(c.key, "desc"),
      label: c.descLabel,
      key: c.key,
      dir: "desc" as const,
    },
    {
      value: sortOptionValue(c.key, "asc"),
      label: c.ascLabel,
      key: c.key,
      dir: "asc" as const,
    },
  ]);

// The next direction for a header click on `sortKey`. Desc first on a fresh
// column (biggest / most recent is what an operator wants first), then toggling.
export const nextSortDirection = <K extends string>(
  sortKey: K,
  current: { key: K; dir: SortDirection }
): SortDirection =>
  current.key === sortKey ? (current.dir === "desc" ? "asc" : "desc") : "desc";
